// Muestreo de trazas por ruta, compartido por los tres inits
// (instrumentation-client.ts, sentry.server.config.ts, sentry.edge.config.ts) —
// mismo patrón que beforeSend/pii-scrubber.
//
// Por qué existe (auditoría 2026-08-01): con `tracesSampleRate: 0.1` plano se
// ingirieron 5.1M spans en 14 días, se reventó el volumen reservado y Sentry
// dejó de recolectar trazas hasta el 11 de agosto. El reparto real era:
//
//   POST /[slug]/pos            1.2M requests → 22M spans  (~18 spans/request)
//   GET  /[slug]/dashboard      1.1M requests → 4.8M spans
//   GET  /api/cron/health-evaluate 18K runs   → 3.4M spans (~190 spans/run)
//
// Es decir: el 70% de la factura la paga el polling del POS (FOHPanel sondea
// `getActiveOrders` cada 5s y cada server action de Next re-renderiza en el
// servidor root layout + [slug] + (pos) + page), y un 11% lo paga un cron que
// corre cada minuto. Ninguno de los dos es una traza que alguien vaya a mirar.
//
// Una tasa plana no distingue esos dos casos del cobro de una tarjeta. Este
// sampler sí.
//
// IMPORTANTE — esto NO afecta a los errores. `tracesSampler` solo decide qué
// TRAZAS se envían; los eventos de error y los warnings (`captureException` /
// `captureMessage`, incluidos los `[stuck-orders]` y `[online-orders] reconcile`
// de los crons) siguen yendo al 100%. Bajar el muestreo apaga el perfilado de
// rendimiento, nunca el monitoreo de fallas.

/** Tasas por clase de ruta. Nombradas para que el test falle si alguien las mueve sin querer. */
export const TRACE_SAMPLE_RATES = {
  /** Crons: trabajo de fondo, sin usuario esperando. Sus fallas ya llegan como eventos de error. */
  cron: 0,
  /** Endpoints de sondeo: se llaman en bucle por diseño; una muestra mínima basta para ver latencia. */
  poll: 0.005,
  /** Camino del dinero: cobro, reembolso, webhooks de North, caja. Cobertura alta a propósito. */
  money: 0.1,
  /** Todo lo demás, incluido el grueso de server actions del POS y del dashboard. */
  standard: 0.02,
} as const

/**
 * Rutas del camino del dinero. `record_sale` y sus hermanas (`checkout_tab`,
 * `append_tab_round`) se invocan desde RUTAS API, no desde los server actions del
 * POS — por eso bajar `POST /[slug]/pos` a `standard` no reduce la visibilidad
 * sobre el cobro. Verificado 2026-08-01 contra app/api/**.
 */
const MONEY_PREFIXES = [
  "/api/orders",
  "/api/online-orders",
  "/api/payments",
  "/api/webhooks/",
  "/api/billing/",
  "/api/cash-",
  "/api/bar-cash-sessions",
  "/api/bar-sales/",
  "/api/north/",
  "/api/delivery/",
  "/api/kiosk/pax-success",
  "/api/receipt/",
  "/api/reconciliation/",
  "/api/hardware/orders",
] as const

/**
 * Endpoints que se sondean en bucle. Se evalúan ANTES que MONEY_PREFIXES porque
 * dos de ellos viven bajo un prefijo de dinero: son solo lecturas de estado
 * ("¿ya se aprobó el cobro?"), no la transacción en sí.
 */
const POLL_PATHS = [
  "/api/orders/[id]/status",
  "/api/online-orders/intent-status",
  "/api/build-id",
] as const

/**
 * Quita el verbo HTTP del nombre de transacción. Sentry nombra las transacciones
 * de servidor como "GET /api/orders" y las de cliente como "/[slug]/pos".
 */
function pathOf(name: string): string {
  const trimmed = name.trim()
  const space = trimmed.indexOf(" ")
  const path = space === -1 ? trimmed : trimmed.slice(space + 1)
  return path.toLowerCase()
}

/** Normaliza un id concreto a su forma paramétrica: /api/orders/abc-123/status → /api/orders/[id]/status */
function normalizeIds(path: string): string {
  return path.replace(/\/[0-9a-f]{8}-[0-9a-f-]{4,}/gi, "/[id]").replace(/\/\d+(?=\/|$)/g, "/[id]")
}

/** La tasa que corresponde a un nombre de transacción. Exportada para poder testearla sin el SDK. */
export function sampleRateForTransaction(name: string): number {
  const path = normalizeIds(pathOf(name))

  if (path.startsWith("/api/cron/")) return TRACE_SAMPLE_RATES.cron
  if (POLL_PATHS.some((p) => path.startsWith(p))) return TRACE_SAMPLE_RATES.poll
  if (MONEY_PREFIXES.some((p) => path.startsWith(p))) return TRACE_SAMPLE_RATES.money

  return TRACE_SAMPLE_RATES.standard
}

/**
 * `tracesSampler` de Sentry. Devuelve una tasa fija por ruta y NO usa
 * `inheritOrSampleWith`: heredar la decisión del padre haría que una traza de
 * cliente muestreada al 2% arrastrara al servidor por debajo del 10% en el
 * camino del dinero, que es justo lo que queremos proteger.
 *
 * No heredar no rompe las trazas distribuidas en la dirección que importa:
 * Sentry propaga un `sample_rand` estable por traza, así que con la misma
 * semilla `r` una ruta al 10% muestrea siempre que una al 2% lo haya hecho
 * (r < 0.02 ⇒ r < 0.1). Lo que el cliente capture, el servidor lo captura.
 */
export function sentryTracesSampler({ name }: { name: string }): number {
  return sampleRateForTransaction(name)
}
